import { useEffect, useRef, useState, type ReactNode } from 'react';

// ===========================================================================
// Button
// ===========================================================================

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  children,
  variant = 'secondary',
  loading = false,
  disabled,
  className = '',
  ...rest
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-series-1 text-white hover:brightness-110',
    secondary: 'bg-raised text-ink hover:bg-baseline ring-1 ring-hairline',
    ghost: 'text-ink-secondary hover:bg-raised hover:text-ink',
    danger: 'bg-critical text-white hover:brightness-110',
  };

  return (
    <button
      className={`${base} ${variants[variant]} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && (
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

/**
 * A resource's own page in the AWS console, in a new tab. The console asks for
 * its own sign-in; the hub gives no access to it.
 */
export function AwsLink({ href, what }: { href?: string | null; what: string }) {
  if (!href) return null;
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      title={`This ${what}'s page in the AWS console`}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-ink-secondary ring-1 ring-hairline hover:bg-raised hover:text-ink"
    >
      Open AWS {what} ↗
    </a>
  );
}

// ===========================================================================
// Card
// ===========================================================================

export function Card({
  children,
  className = '',
  title,
  action,
  id,
}: {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  action?: ReactNode;
  /** An anchor, for scrolling the card into view. */
  id?: string;
}) {
  return (
    <div
      id={id}
      className={`rounded-lg bg-surface ring-1 ring-hairline ${className}`}
    >
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-grid px-4 py-3">
          {typeof title === 'string' ? (
            <h2 className="text-sm font-semibold text-ink">{title}</h2>
          ) : (
            title
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// ===========================================================================
// Status pill
// ===========================================================================

/**
 * Status is never communicated by colour alone — the dot carries the colour,
 * the text carries the meaning. That is what makes this readable to a
 * colour-blind operator and in a screenshot printed in black and white.
 */
export function StatusPill({ state, pending }: { state: string; pending?: string | null }) {
  const colours: Record<string, string> = {
    running: 'var(--color-good)',
    pending: 'var(--color-warning)',
    stopping: 'var(--color-warning)',
    'shutting-down': 'var(--color-warning)',
    stopped: 'var(--color-ink-muted)',
    terminated: 'var(--color-critical)',
  };

  const label = pending ? `${pending}…` : state;
  const isTransitional = pending || ['pending', 'stopping', 'shutting-down'].includes(state);

  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${isTransitional ? 'animate-pulse-soft' : ''}`}
        style={{ background: colours[state] ?? 'var(--color-ink-muted)' }}
      />
      <span className="capitalize text-ink-secondary">{label}</span>
    </span>
  );
}

// ===========================================================================
// Stat tile
// ===========================================================================

export function StatTile({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'good' | 'warning' | 'critical';
}) {
  const tones = {
    default: 'var(--color-ink)',
    good: 'var(--color-good)',
    warning: 'var(--color-warning)',
    critical: 'var(--color-critical)',
  };

  return (
    <div className="rounded-lg bg-surface px-4 py-3 ring-1 ring-hairline">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-muted">
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold sm:text-2xl" style={{ color: tones[tone] }}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-ink-secondary">{sub}</div>}
    </div>
  );
}

// ===========================================================================
// Modal
// ===========================================================================

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    if (!open) return;

    function onKey(event: KeyboardEvent) {
      // Escape closes, unless it is dismissing something of the field's own
      // (an open select or an IME composition).
      if (event.key === 'Escape' && !event.defaultPrevented && !(event as KeyboardEvent & { isComposing?: boolean }).isComposing) onClose();
    }
    document.addEventListener('keydown', onKey);

    // Stop the page scrolling behind the dialog.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      // Close on a click on the backdrop and nothing else. A click's target is
      // where the press began and ended together; a press that begins in a
      // field (selecting its text, dragging a slider) and ends past the
      // dialog's edge fires on the backdrop too, and that used to throw the
      // whole form away.
      onPointerDown={(event) => { pressedBackdrop.current = event.target === event.currentTarget; }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pressedBackdrop.current) onClose();
        pressedBackdrop.current = false;
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${width} rounded-lg bg-surface ring-1 ring-hairline shadow-2xl`}
      >
        <div className="flex items-center justify-between border-b border-grid px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[calc(100dvh-9rem)] overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-grid px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Form fields
// ===========================================================================

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-secondary">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
      {error && (
        <span className="mt-1 block text-xs" style={{ color: 'var(--color-critical)' }}>
          {error}
        </span>
      )}
    </label>
  );
}

const inputClass =
  'w-full rounded-md bg-plane px-3 py-2 text-sm text-ink ring-1 ring-hairline placeholder:text-ink-muted focus:ring-series-1';

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${inputClass} ${props.className ?? ''}`} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${inputClass} ${props.className ?? ''}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${inputClass} ${props.className ?? ''}`} />;
}

// ===========================================================================
// Feedback
// ===========================================================================

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning' | 'critical' | 'good';
  title?: string;
  children: ReactNode;
}) {
  const tones = {
    info: { colour: 'var(--color-series-1)', icon: 'ℹ' },
    warning: { colour: 'var(--color-warning)', icon: '⚠' },
    critical: { colour: 'var(--color-critical)', icon: '✕' },
    good: { colour: 'var(--color-good)', icon: '✓' },
  };

  const { colour, icon } = tones[tone];

  return (
    <div
      className="flex gap-2 rounded-md px-3 py-2 text-sm"
      style={{ background: `color-mix(in srgb, ${colour} 12%, transparent)` }}
    >
      {/* Icon plus text, so the tone never depends on colour alone. */}
      <span aria-hidden style={{ color: colour }}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        {title && <div className="font-medium">{title}</div>}
        <div className="text-ink-secondary">{children}</div>
      </div>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="px-4 py-12 text-center">
      <div className="text-sm font-medium text-ink-secondary">{title}</div>
      {children && <div className="mt-1 text-xs text-ink-muted">{children}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-ink-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      {label ?? 'Loading…'}
    </div>
  );
}

/** Reveals a secret on demand, so passwords are not sitting on screen by default. */
export function SecretReveal({ value, label }: { value: string; label: string }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded bg-plane px-2 py-1.5 font-mono text-xs">
        {shown ? value : '•'.repeat(Math.min(value.length, 24))}
      </code>
      <Button variant="ghost" onClick={() => setShown((s) => !s)}>
        {shown ? 'Hide' : 'Show'}
      </Button>
      <Button
        variant="ghost"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        aria-label={`Copy ${label}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

/**
 * "Are you sure?" for anything that takes a feed away. One line of
 * consequence, a plain cancel, and a button that says the verb.
 */
export function Confirm({
  open,
  title,
  message,
  confirmLabel = 'Yes',
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      }
    >
      <p className="text-sm text-ink-secondary">{message}</p>
    </Modal>
  );
}
